import React, { useCallback } from 'react';

import {
  APIHelpRequestType,
  APIProviderIntegrationStatus,
  APIProviderIntegrationStatusModel,
} from '@cd3p/core/types/api';
import { WifiOffOutlined, WifiOutlined } from '@mui/icons-material';
import { Badge, Box, Tooltip, Typography } from 'components';

import { dayjs, styled, useTheme, useTranslation } from 'third-party';

import { ContactSupportButton } from 'features/HelpCenter/ContactSupport/ContactSupportButton';

const IntegrationStatusIconContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 12px;
  font-size: 0.825rem;
`;

const BadgeContainer = styled(Badge)`
  & .MuiBadge-badge {
    top: 0;
    right: 0;
    transform: translate(3px, 0.1px);
    padding: 0;
    min-width: 14px;
    height: 14px;
    font-size: 0.6rem;
  }
`;

type IntegrationStatusIconProps = {
  integrationStatusModel: APIProviderIntegrationStatusModel;
};

export const IntegrationStatusIcon: React.FC<IntegrationStatusIconProps> = ({
  integrationStatusModel,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const lastSyncDate = dayjs(integrationStatusModel?.lastSyncDate).format(
    'YYYY-MM-DD HH:mm:ss',
  );

  const syncNotStarted =
    integrationStatusModel.status === APIProviderIntegrationStatus.NotStarted ||
    (integrationStatusModel.status === APIProviderIntegrationStatus.Failed &&
      !integrationStatusModel.lastSyncDate);

  //For now we assume that Failed is the same as Online
  const isOnline =
    integrationStatusModel.status === APIProviderIntegrationStatus.Online ||
    integrationStatusModel.status === APIProviderIntegrationStatus.Failed;

  const getColor = useCallback(() => {
    if (syncNotStarted) {
      return theme.custom.palette.muted600;
    }
    if (isOnline) {
      return theme.custom.palette.primary900;
    }
    return theme.custom.palette.danger700;
  }, [syncNotStarted, isOnline, theme]);

  const getBgColor = useCallback(() => {
    if (syncNotStarted) {
      return theme.custom.palette.muted50;
    }
    if (isOnline) {
      return theme.custom.palette.primary50;
    }
    return theme.custom.palette.warning50;
  }, [syncNotStarted, isOnline, theme]);

  return (
    <>
      <IntegrationStatusIconContainer
        px={1.5}
        py={0.5}
        bgcolor={getBgColor()}
        color={getColor()}
      >
        {syncNotStarted ? (
          <>
            <WifiOffOutlined fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="body2" fontWeight={500}>
              {t('settings.integrations.form.notSynced')}
            </Typography>
          </>
        ) : (
          <>
            <Tooltip
              title={t('settings.integrations.form.lastSynced', {
                date: lastSyncDate,
              })}
            >
              {isOnline ? (
                <BadgeContainer badgeContent="✔" color="success">
                  <WifiOutlined fontSize="small" sx={{ mr: 0.5 }} />
                </BadgeContainer>
              ) : (
                <BadgeContainer badgeContent="✖" color="error">
                  <WifiOffOutlined fontSize="small" sx={{ mr: 0.5 }} />
                </BadgeContainer>
              )}
            </Tooltip>
            <Typography variant="body2" fontWeight={500}>
              {isOnline
                ? t('settings.integrations.form.active')
                : t('settings.integrations.form.disconnected')}
            </Typography>
          </>
        )}
      </IntegrationStatusIconContainer>
      {!isOnline && !syncNotStarted && (
        <ContactSupportButton
          preselectedSubject={APIHelpRequestType.ConnectionIssue}
        />
      )}
    </>
  );
};
